// Native capabilities bridge — every export is a no-op on the web (isNative guard),
// so the existing website is completely untouched by the Capacitor layer.
import { Capacitor } from "@capacitor/core";

// True inside the Capacitor app; "p1_native_preview" localStorage flag or "?native=1"
// query param forces the native presentation for preview/screenshot verification in a browser.
// The query param is persisted to localStorage so the flag survives hard reloads (login uses
// window.location.assign, which drops the query string).
export const isNative = () => {
  if (Capacitor.isNativePlatform()) return true;
  if (typeof window === "undefined") return false;
  try {
    if (window.localStorage?.getItem("p1_native_preview") === "1") return true;
    if (new URLSearchParams(window.location?.search || "").get("native") === "1") {
      window.localStorage?.setItem("p1_native_preview", "1");
      return true;
    }
    return false;
  } catch { return false; }
};

// The remote web bundle can be redeployed without the installed APK changing, so
// native capabilities must be probed against the installed binary before use.
export const nativeHasPlugin = (name) => {
  try {
    return Capacitor.isNativePlatform() && Capacitor.isPluginAvailable(name);
  } catch {
    return false;
  }
};

const parseVer = (v) => String(v || "0").split(".").map((n) => parseInt(n, 10) || 0);
const versionAtLeast = (v, min) => {
  const a = parseVer(v);
  const b = parseVer(min);
  for (let i = 0; i < 3; i++) {
    if ((a[i] || 0) !== (b[i] || 0)) return (a[i] || 0) > (b[i] || 0);
  }
  return true;
};

// CRASH FIX (v1.0 APK): PushNotifications.register() calls FirebaseMessaging.getInstance();
// binaries built without google-services.json (v1.0) have no FirebaseApp, so the plugin
// throws IllegalStateException, which the Capacitor bridge rethrows as a RuntimeException
// on its worker thread — killing the app (a JS try/catch cannot intercept that).
// Only native binaries >= this version ship Firebase; older installs must never call it.
const PUSH_MIN_NATIVE_VERSION = "1.1";

// Persistent per-installation id: the FCM token belongs to the app installation, and
// login/logout only change which account the installation is associated with.
const INSTALL_KEY = "p1_install_id";
export const getInstallId = () => {
  try {
    let v = localStorage.getItem(INSTALL_KEY);
    if (!v) {
      v = (typeof crypto !== "undefined" && crypto.randomUUID)
        ? crypto.randomUUID()
        : `inst-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      localStorage.setItem(INSTALL_KEY, v);
    }
    return v;
  } catch { return "unknown-install"; }
};

// Last token successfully posted to the backend. When the server flags this installation
// as unregistered, THIS cached value is the one FCM rejected — the heal loop must not
// accept (or re-post) the same value again.
const LAST_TOKEN_KEY = "p1_last_token";

// While a heal cycle is running, the registration listener must not POST anything —
// the heal orchestrator posts the accepted fresh token itself. healBlocked holds the
// rejected token AND the pre-delete cached token: the FCM SDK clears its local cache
// only after the delete is server-acknowledged, so any event carrying a blocked value
// means the deletion hasn't landed yet and the token must not be trusted or re-posted.
let healInProgress = false;
let healBlocked = new Set();
let lastTokenPost = Promise.resolve();

// Lets syncPushToken await the asynchronous 'registration'/'registrationError' events
// that the plugin emits after register() resolves (register() never returns the token).
let tokenWaiter = null;
const resolveTokenWaiter = (v) => {
  const w = tokenWaiter;
  tokenWaiter = null;
  if (w) w(v);
};
const waitForTokenEvent = (ms) =>
  new Promise((resolve) => {
    const done = (v) => {
      clearTimeout(timer);
      if (tokenWaiter === done) tokenWaiter = null;
      resolve(v);
    };
    const timer = setTimeout(() => done(null), ms);
    tokenWaiter = done;
  });

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Safe client-side token fingerprint — correlates with the backend register log's head=.
const clientTokenFp = (t) => `len=${(t || "").length} head=${(t || "").slice(0, 8)}`;

// Client-side diagnostic breadcrumb: the native unregister/register steps are invisible
// without logcat, so each one is beaconed to the backend (best-effort, never throws).
const trace = (step, detail = "") => {
  try {
    import("../lib/api")
      .then(({ default: api }) =>
        api.post("/devices/trace", {
          installation_id: getInstallId(),
          step,
          detail: String(detail).slice(0, 300),
        })
      )
      .catch(() => {});
  } catch { /* diagnostics must never break the flow */ }
};

// After login / session restore: re-register the current token and (re)associate this
// installation with the now-authenticated account. (Pre-login registration attempts get a
// 401 and are corrected here; FCM token refresh flows through the same registration listener.)
export async function syncPushToken() {
  if (!Capacitor.isNativePlatform()) return;
  try {
    const { App } = await import("@capacitor/app");
    const info = await App.getInfo();
    if (!versionAtLeast(info?.version, PUSH_MIN_NATIVE_VERSION) || !nativeHasPlugin("PushNotifications")) return;
    const { PushNotifications } = await import("@capacitor/push-notifications");
    const perm = await PushNotifications.checkPermissions();
    if (perm.receive !== "granted") return; // never nag here; the ask happens at first launch
    const api = (await import("../lib/api")).default;
    let registered = true;
    try {
      const st = await api.get("/devices/status", { params: { installation_id: getInstallId() } });
      registered = !!st.data?.registered;
    } catch { registered = true; /* offline/older backend: keep the plain register path */ }
    trace("status_checked", `registered=${registered}`);
    if (registered) {
      await PushNotifications.register(); // healthy path: refresh + re-associate
      return;
    }
    // Self-heal. The plugin's unregister() fires FirebaseMessaging.deleteToken()
    // WITHOUT awaiting it (and swallows its errors), and a late-landing delete keyed to
    // this app instance kills whatever registration is current when it executes — the
    // likely reason previously "fresh" tokens died minutes after minting. The FCM SDK
    // clears its local cache only once the delete is server-acknowledged, so: capture
    // the cached token FIRST, then delete, then re-register in a loop and accept ONLY a
    // token that differs from both the rejected one and the pre-delete cached one.
    const stale = (() => { try { return localStorage.getItem(LAST_TOKEN_KEY) || ""; } catch { return ""; } })();
    trace("heal_start", `stale ${clientTokenFp(stale)}`);
    healInProgress = true;
    healBlocked = new Set([stale].filter(Boolean));
    try {
      let cached = "";
      const capture = waitForTokenEvent(6000);
      try { await PushNotifications.register(); } catch { /* event may still arrive */ }
      cached = (await capture) || "";
      if (cached) healBlocked.add(cached);
      trace("heal_cached_token", clientTokenFp(cached));
      try { await PushNotifications.unregister(); } catch { /* no cached token to drop */ }
      trace("unregister_called", "deleteToken is fire-and-forget; waiting for the SDK cache to clear");
      let fresh = "";
      for (const settle of [3000, 6000, 10000]) {
        await sleep(settle);
        const pending = waitForTokenEvent(8000);
        try { await PushNotifications.register(); } catch { /* event may still arrive */ }
        const tok = await pending;
        if (tok && !healBlocked.has(tok)) {
          fresh = tok;
          trace("heal_fresh_token", clientTokenFp(tok));
          break;
        }
        trace("heal_retry", tok ? `pre-delete token still cached ${clientTokenFp(tok)}` : "no token event");
      }
      if (fresh) {
        try {
          await api.post("/devices/push-token", {
            token: fresh,
            platform: Capacitor.getPlatform(),
            installation_id: getInstallId(),
          });
          try { localStorage.setItem(LAST_TOKEN_KEY, fresh); } catch { /* private mode */ }
          trace("token_posted", clientTokenFp(fresh));
        } catch (e) {
          trace("token_post_failed", String(e?.response?.status || e?.message || "error"));
        }
        try {
          const st = await api.get("/devices/status", { params: { installation_id: getInstallId() } });
          trace("heal_verify", `registered=${!!st.data?.registered}`);
        } catch { /* status re-check is best-effort */ }
      } else {
        trace("heal_exhausted", "SDK never produced a post-delete token; retry on next launch");
      }
    } finally {
      healInProgress = false;
      healBlocked = new Set();
    }
  } catch { /* push stays off for this binary */ }
}

// On logout: detach this installation from the account server-side so the previous
// account's notifications stop reaching this device.
export async function deactivatePushInstallation() {
  if (!Capacitor.isNativePlatform()) return;
  try {
    const api = (await import("../lib/api")).default;
    await api.post("/devices/deactivate", { installation_id: getInstallId() });
  } catch { /* next login re-associates anyway */ }
}

export async function initNative() {
  if (!Capacitor.isNativePlatform()) return;
  document.documentElement.classList.add("native-app");
  const { App } = await import("@capacitor/app");
  const { StatusBar, Style } = await import("@capacitor/status-bar");
  const { SplashScreen } = await import("@capacitor/splash-screen");
  const { Network } = await import("@capacitor/network");

  StatusBar.setOverlaysWebView({ overlay: false }).catch(() => {});
  StatusBar.setStyle({ style: Style.Light }).catch(() => {});
  StatusBar.setBackgroundColor({ color: "#064E3B" }).catch(() => {});
  SplashScreen.hide().catch(() => {});

  // Android hardware back: walk history like a native app; exit only at the entry screens.
  App.addListener("backButton", () => {
    const p = window.location.pathname;
    if (p === "/" || p === "/login" || p === "/app") App.exitApp();
    else window.history.back();
  });

  // Deep links: App Links (https://parentonecare.com/...) and any custom-scheme URL
  // open inside the app on the right screen (WhatsApp/email/notification links).
  App.addListener("appUrlOpen", ({ url }) => {
    try {
      const u = new URL(url);
      if (u.host.endsWith("parentonecare.com")) window.location.href = u.pathname + u.search + u.hash;
    } catch { /* ignore malformed URLs */ }
  });

  // Offline indicator (adds a class; layout CSS shows a subtle banner).
  Network.addListener("networkStatusChange", (s) => {
    document.body.classList.toggle("p1-offline", !s.connected);
  });

  // Push notifications: gated on the INSTALLED BINARY version, because register()
  // crashes binaries that lack Firebase config (see PUSH_MIN_NATIVE_VERSION).
  try {
    const info = await App.getInfo();
    if (versionAtLeast(info?.version, PUSH_MIN_NATIVE_VERSION) && nativeHasPlugin("PushNotifications")) {
      const { PushNotifications } = await import("@capacitor/push-notifications");
      const perm = await PushNotifications.requestPermissions();
      if (perm.receive === "granted") {
        // No eager register() here: pre-login the POST would 401 anyway, and blindly
        // re-posting the SDK's cached token can reactivate a server-invalidated row
        // before syncPushToken's status check runs. Registration is orchestrated by
        // syncPushToken once an authenticated session exists.
        PushNotifications.addListener("registration", async (token) => {
          const value = token?.value || "";
          resolveTokenWaiter(value || null);
          if (!value) return;
          if (healInProgress) {
            // Mid-heal the orchestrator owns posting; pre-delete/blocked tokens must
            // never reach the backend (they would reactivate a dead row).
            trace(healBlocked.has(value) ? "token_event_stale_skipped" : "token_event", clientTokenFp(value));
            return;
          }
          lastTokenPost = (async () => {
            try {
              const api = (await import("../lib/api")).default;
              await api.post("/devices/push-token", {
                token: value,
                platform: Capacitor.getPlatform(),
                installation_id: getInstallId(),
              });
              try { localStorage.setItem(LAST_TOKEN_KEY, value); } catch { /* private mode */ }
              trace("token_posted", clientTokenFp(value));
            } catch (e) {
              trace("token_post_failed", String(e?.response?.status || e?.message || "error"));
            }
          })();
          await lastTokenPost;
        });
        PushNotifications.addListener("registrationError", (err) => {
          trace("registration_error", String(err?.error || err || "unknown"));
          resolveTokenWaiter(null);
        });
        PushNotifications.addListener("pushNotificationActionPerformed", (ev) => {
          const target = ev.notification?.data?.url;
          if (target) window.location.href = target;
        });
      }
    }
  } catch { /* push not available on this binary */ }
}

// Camera / gallery capture → File, feeding the existing document-upload flow.
export async function takePhoto() {
  if (!nativeHasPlugin("Camera")) return null;
  const { Camera, CameraResultType, CameraSource } = await import("@capacitor/camera");
  const photo = await Camera.getPhoto({
    quality: 85,
    resultType: CameraResultType.DataUrl,
    source: CameraSource.Prompt,
    promptLabelHeader: "Add document",
    promptLabelPhoto: "Choose from gallery",
    promptLabelPicture: "Take photo",
  });
  if (!photo?.dataUrl) return null;
  const blob = await (await fetch(photo.dataUrl)).blob();
  return new File([blob], `photo-${Date.now()}.${photo.format || "jpeg"}`, { type: blob.type || "image/jpeg" });
}

// Save a fetched blob into the device Documents folder (native download path).
export async function nativeSaveBlob(blob, filename) {
  const { Filesystem, Directory } = await import("@capacitor/filesystem");
  const base64 = await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(",")[1]);
    r.onerror = reject;
    r.readAsDataURL(blob);
  });
  const out = await Filesystem.writeFile({ path: filename, data: base64, directory: Directory.Documents, recursive: true });
  return out.uri;
}
